import type { Getter } from 'jotai';

import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';

/**
 * Reading the selected brand inside a Jotai query atom.
 *
 * `atomWithQuery` hands its callback a `get`, so the scope is read the same way any other
 * atom dependency is — no prop drilling and no interceptor. An interceptor was the obvious
 * alternative and is the wrong shape here: the Users page must stay unscoped, and an
 * allow-list buried in the HTTP layer puts that rule far away from the code it governs.
 */

/**
 * The query params for the current scope, or nothing at all before a brand has resolved.
 * Spread into a request so `bu_id` is ABSENT rather than empty when unscoped — the backend
 * reads absent as account-wide, which is the behaviour every existing caller relies on.
 */
export const buScopeParams = (get: Getter): { bu_id?: string } => {
  const buId = get(selectedBusinessUnitIdAtom);
  return buId ? { bu_id: buId } : {};
};

/**
 * The scope segment for a query key, so two brands never read each other's cached rows.
 *
 * This is the only thing keeping two brands' rows apart in the cache: the selection change
 * does NOT clear queries any more (that refetched every unscoped list as well and was the
 * slow brand switch), so a key that does not carry the brand will serve the wrong one.
 */
export const buScopeKey = (get: Getter): string => get(selectedBusinessUnitIdAtom) ?? 'unresolved';

/**
 * The same scope, appended to a URL for a WRITE.
 *
 * Reads pass `bu_id` as a request param; writes need it on the URL of a POST whose body is
 * already the payload, so this keeps the two halves using one spelling of the parameter.
 *
 * Unscoped returns the url untouched rather than with an empty `bu_id`, because the
 * backend reads an ABSENT parameter as account-wide and a malformed one the same way —
 * sending `bu_id=` would work by accident rather than by contract.
 *
 * Deliberately explicit at each call site rather than an HTTP interceptor, for the reason
 * in `buScopeParams`: not every write is scoped, and an allow-list buried in the HTTP
 * layer puts that rule far from the code it governs.
 */
export const withBuScope = (url: string, scope: { bu_id?: string }): string => {
  if (!scope.bu_id) return url;
  return `${url}${url.includes('?') ? '&' : '?'}bu_id=${encodeURIComponent(scope.bu_id)}`;
};

/**
 * There is deliberately NO path gate here any more.
 *
 * The switcher used to render only on an allow-list of sections (`/sources`, `/training`,
 * `/test` and home). That was a narrower rule than the product wants: the brand is a GLOBAL
 * scope, the same way the account is, and hiding the control everywhere else made the
 * operator navigate to a scoped page just to change which brand they were looking at.
 *
 * The allow-list existed to keep a brand filter off pages that ignore it, so nobody picks a
 * brand, watches a page refetch and gets identical content back. That trade-off is now made
 * the other way round: a control that is always in the same place beats one that vanishes.
 * A page that does not scope its reads still does not scope them, so treat the switcher as
 * "which brand am I working as", not "this page is filtered".
 */
